import { msg } from "@omegagrid/localize";
import { NodeSource } from "@omegagrid/tree";
import { CellSource, ColumnSource, RowSource } from "@omegagrid/grid";
import rp, { GridEditorAdapter, GridEditorOptions, RowData } from "@reporting/rp-app-host";
import { Thread } from "@reporting/rp-types/etl";
import { ETLJob, JobParam } from "@reporting/rp-types/jobs";
import { JOB_PARAMS } from "../types"

type DataSource = {
	param?: string | CellSource;
	name?: string;
	list?: string;
	value?: CellSource;
};

export class ParamsGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];
	private job: ETLJob;
	private param: JobParam;

	constructor(
		private jobName: string,
	) { super() }

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		this._columns = [{
			data: 'param',
			header: msg('Parameter'),
			t: 't',
			e: false,
			w: 100,
		}, {
			data: 'name',
			header: msg('Name'),
			t: 't',
			e: true,
			w: 200,
		}, {
			data: 'list',
			header: msg('List'),
			t: 's',
			content: await this.getThreadList(),
			e: true,
			w: 200,
		}, {
			data: 'value',
			header: msg('Value'),
			t: 't',
			e: true,
			w: 200,
			z: '0',
		}];
		return this._columns;
	}

	async getThreadList(): Promise<NodeSource[]> {
		const threads = await rp.ms('etl').get<Thread[]>('threads', {
			where: { type: 'L' },
			order: [['description', 'asc']],
			fields: ['id_thread', 'description'],
			scope: 1 // to include master accounting
		});
		const createdFolders = new Set<string>();
		const folderItems: NodeSource[] = [];
		const items: NodeSource[] = [];

		for (const thread of threads) {
			let nodeLvl = 0;
			let threadName = thread.description;
			const nameParts = thread.description.split('/');
			if (nameParts.length > 1) {
				threadName = nameParts.pop();
				nameParts.forEach((folderName, index) => {
					const path = nameParts.slice(0, index + 1).join('/');
					if (!createdFolders.has(path)) {
						createdFolders.add(path);
						folderItems.push({
							k: path,
							v: folderName,
							l: index,
							collapsed: true,
							ch: false,
						});
					}
					nodeLvl++;
				});
			}

			const newItem: NodeSource = {
				k: thread.id_thread.toString(),
				v: threadName,
				l: nodeLvl,
			};
			if (nodeLvl == 0) items.push(newItem);
			else folderItems.push(newItem);
		}
		return [...folderItems, ...items];
	}

	async getRows(): Promise<RowSource[]> {
		this.job = await rp.ms('jobs').get<ETLJob>('jobs', { job_name: this.jobName });
		this.param = await rp.ms('jobs').get<JobParam>('params', { job_name: this.jobName });
		const rows: RowSource[] = [];
		for (const p of JOB_PARAMS) {
			rows.push({ data: {
				param: { v: p.name, dv: p.name.toUpperCase() },
				name: this.job[`description_${p.name}` as keyof ETLJob],
				list: this.job[`id_thread_${p.name}` as keyof ETLJob]?.toString(), // toString() for NodeSource key type
				value: { v: this.param[p.name] ?? null, t: p.type },
			} as unknown as DataSource });
		}
		return rows;
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			rowNumbers: false,
			columnSorting: false,
			filters: false,
			refreshAfterSave: true,
			saveMode: 'edited_rows',
			pageSize: null,
			toolbar: {
				new: { enabled: false },
			},
		});
	}

	async save(rowData: RowData[]) {
		const data = rowData.map(row => (row.data as DataSource));
		if (!this.param) this.param = { param: this.jobName };
		for (const d of data) {
			const paramKey = d.param as string;
			(this.job as any)[`description_${paramKey}`] = d.name;
			(this.job as any)[`id_thread_${paramKey}`] = d.list ? parseInt(d.list) : null;
			(this.param as any)[paramKey] = d.value;
		}
		await rp.ms('jobs').post('jobs', this.job);
		await rp.ms('jobs').post('params', this.param);
	}

}
